import { Component, OnInit, effect, inject, input, output, untracked } from '@angular/core';
import { MatIconButton } from '@angular/material/button';
import { MatTooltip } from '@angular/material/tooltip';
import { marker } from '@colsen1991/ngx-translate-extract-marker';
import { IimAutocompleteFilterable, IimAutocompleteModule } from '@iim-angular/components';
import { NotificationMessageLevel, NotificationService } from '@lib/core';
import { I18nService } from '@lib/i18n';
import { CustomerIncoterm } from '@lib/incoterms';
import { ConfirmationService } from '@lib/util';
import { TranslateModule } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import { DemandIncotermDataService } from './demand-incoterm-data.service';
import { DemandSupplierIncotermStore } from './demand-supplier-incoterm.store';

@Component({
    selector: 'demand-supplier-incoterm',
    imports: [IimAutocompleteModule, MatIconButton, MatTooltip, TranslateModule],
    templateUrl: './demand-supplier-incoterm.component.html',
    styleUrl: './demand-supplier-incoterm.component.scss',
    providers: [DemandSupplierIncotermStore],
})
export class DemandSupplierIncotermComponent implements OnInit {
    public readonly store = inject(DemandSupplierIncotermStore);
    private readonly demandIncotermDataService = inject(DemandIncotermDataService);
    private readonly notificationService = inject(NotificationService);
    private readonly confirmationService = inject(ConfirmationService);
    private readonly i18nService = inject(I18nService);

    public readonly demandId = input.required<string>();
    public readonly supplierCompanyId = input.required<string>();
    public readonly incotermCode = input<string | null>(null);
    public readonly defaultIncotermCode = input<string | null>(null);
    public readonly editable = input(false);

    public readonly updated = output<string | null>();

    public constructor() {
        effect(() => {
            const incotermCode = this.incotermCode();
            const defaultIncotermCode = this.defaultIncotermCode();
            const editable = this.editable();
            untracked(() => this.store.setSelection(incotermCode, defaultIncotermCode, editable));
        });
    }

    public async ngOnInit(): Promise<void> {
        await this.store.loadIncoterms();
    }

    public onIncotermSelected(selected?: IimAutocompleteFilterable<CustomerIncoterm>): void {
        if (selected) {
            this.update(selected.value.shortCode);
        }
    }

    public async deleteCurrent($event: Event): Promise<void> {
        $event.stopPropagation();
        if (!this.editable() || !this.incotermCode()) {
            return;
        }
        const confirmed = await firstValueFrom(
            this.confirmationService.confirm(this.i18nService.translateInstant(marker('Incoterm wirklich entfernen?'))),
        );
        if (!confirmed) {
            return;
        }
        this.persist(null);
    }

    public update(code: string | null): void {
        if (this.editable()) {
            this.persist(code);
        }
    }

    private persist(code: string | null): void {
        const normalizedCode = code?.trim().toUpperCase() || null;
        // No explicit selection is stored while the supplier default applies, so a later change of
        // that default keeps flowing through.
        const storedCode = normalizedCode === this.defaultIncotermCode() ? null : normalizedCode;
        const request$ = storedCode
            ? this.demandIncotermDataService.updateIncoterm(this.demandId(), this.supplierCompanyId(), storedCode)
            : this.demandIncotermDataService.deleteIncoterm(this.demandId(), this.supplierCompanyId());

        request$.subscribe(() => {
            this.updated.emit(storedCode);
            this.notificationService.showNotification(
                storedCode ? marker('Incoterm gespeichert') : marker('Incoterm entfernt'),
                NotificationMessageLevel.SUCCESS,
            );
        });
    }
}
